import { useCallback } from 'react'
import { Stack } from '@jarvis/web-stratus-client'
import { useHistory } from 'react-router-dom'
import { useNextStage } from './useNextStage'
import { useStack } from './useStack'
import { useSignupSession } from './useSignupSession'
import { useSignupState } from './useSignupState'
import { useDispatch } from './useDispatch'
import { exitSignupSession } from '../actions'
import { useHpAdminUrl } from './useHpAdminUrl'
import { useFlowContext } from './useFlowContext'
import { wait } from '../lib/helpers'
import { useOnboardingSession } from './useOnboarding'

const logWarning = (stack: Stack, message?: string) => {
  if (stack != Stack.prod && message) {
    console.warn(message)
  }
}

const testAlert = (fromTest: boolean, stack: Stack, message: () => string) => {
  if (fromTest && stack != Stack.prod) {
    alert(message())
  }
}

const exitToInstantInk = (hpAdminUrl: string, initiator?: string) => {
  if (initiator === 'dashboard') {
    window.close()
    wait(2500).then(() => {
      window.location.replace(hpAdminUrl)
    })
  } else {
    window.location.replace(hpAdminUrl)
  }
}

export const useExitFlow = () => {
  const history = useHistory()
  const nextStage = useNextStage()
  const dispatch = useDispatch()
  const hpAdminUrl = useHpAdminUrl()
  const onboardingSession = useOnboardingSession()
  const stack = useStack()
  const {
    exitFlowToInstantInk,
    exitWithAppCallback
  } = useFlowContext().getOptions()
  const {
    xCorrelationId,
    redirectUri,
    accountIdentifier,
    initiator,
    isODFlow,
    fromTest,
    mnsCallbacks
  } = useSignupSession()
  const { partialSubscription } = useSignupState()
  const subscriptionId =
    accountIdentifier ||
    partialSubscription.data?.replacementData?.printer?.accountIdentifier

  return useCallback(
    async (result: string, output = {}, errorInfo = {}) => {
      logWarning(stack, output?.errorMessage)

      if (exitFlowToInstantInk) {
        await dispatch(exitSignupSession())
        exitToInstantInk(hpAdminUrl, initiator)
        return
      }

      const programId =
        onboardingSession?.derivedAttributes?.catalogMgtSvc?.find(
          (item) => item.serviceId === 'UCDEplus'
        )?.programId ?? undefined

      const { selectedLoginType, ...outputWithoutSelectedLoginType } = output

      const userPayload = () => {
        if (programId !== undefined || output.selectedLoginType) {
          return {
            user: {
              ...(programId !== undefined && { selectedProgramId: programId }),
              ...(selectedLoginType && {
                selectedLoginType: selectedLoginType
              })
            }
          }
        }
      }

      const outputPayload =
        result === 'success'
          ? {
              ...outputWithoutSelectedLoginType,
              ...userPayload(),
              ...(subscriptionId !== undefined && { subscriptionId })
            }
          : {
              ...outputWithoutSelectedLoginType
            }

      if (!isODFlow) {
        await dispatch(exitSignupSession())
        testAlert(fromTest, stack, () =>
          JSON.stringify({
            result,
            output: outputPayload,
            errorInfo,
            xCorrelationId
          })
        )

        if (!redirectUri) {
          history.push('/')
          return
        }
      }

      if (exitWithAppCallback) {
        window.location.replace(mnsCallbacks?.exitRedirectUrl)
        return
      }

      if (redirectUri) {
        history.push(
          redirectUri,
          subscriptionId ? { subscriptionId } : undefined
        )
      } else if (nextStage) {
        await nextStage?.call(null, {
          result,
          output: outputPayload,
          errorInfo,
          xCorrelationId
        })
      }
    },
    [
      stack,
      initiator,
      exitFlowToInstantInk,
      isODFlow,
      redirectUri,
      nextStage,
      dispatch,
      hpAdminUrl,
      history,
      subscriptionId,
      xCorrelationId,
      fromTest,
      onboardingSession?.derivedAttributes?.catalogMgtSvc,
      exitWithAppCallback,
      mnsCallbacks
    ]
  )
}
